import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useApp } from '../contexts/AppContext';
import { Header as StyledHeader, HeaderTitle, HeaderActions } from './styled/Layout';
import { PrimaryButton, SecondaryButton } from './styled/Button';
import { Flex } from './styled/Layout';
import apiService from '../services/apiService';

const Header = () => {
  const { user, logout } = useAuth();
  const { activeApp } = useApp();
  const location = useLocation();
  const navigate = useNavigate();
  const isShopRoute = location.pathname.startsWith('/shop');
  const [cartCount, setCartCount] = useState(0);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [categories, setCategories] = useState([]);
  const [showCategoryMenu, setShowCategoryMenu] = useState(false);
  const userMenuRef = useRef(null);
  const categoryMenuRef = useRef(null);

  // Impersonation detection
  const isImpersonating = !!sessionStorage.getItem('impersonation_original_token');
  const originalUserName = sessionStorage.getItem('impersonation_original_name');

  const handleExitImpersonation = () => {
    const originalToken = sessionStorage.getItem('impersonation_original_token');
    const originalRefresh = sessionStorage.getItem('impersonation_original_refresh');
    if (originalToken) {
      localStorage.setItem('accessToken', originalToken);
      if (originalRefresh) localStorage.setItem('refreshToken', originalRefresh);
      sessionStorage.removeItem('impersonation_original_token');
      sessionStorage.removeItem('impersonation_original_refresh');
      sessionStorage.removeItem('impersonation_original_name');
      window.location.reload();
    }
  };

  // Fetch categories for shop routes
  useEffect(() => {
    if (!isShopRoute) return;

    const fetchCategories = async () => {
      try {
        const response = await fetch(`${import.meta.env.VITE_API_URL || ''}/shop/categories`);
        const data = await response.json();
        setCategories(data || []);
      } catch (error) {
        console.error('Error fetching categories:', error);
        setCategories([]);
      }
    };

    fetchCategories();
  }, [isShopRoute]);

  // Listen for cart changes
  useEffect(() => {
    if (!isShopRoute) return;

    const updateCartCount = () => {
      try {
        const cart = JSON.parse(localStorage.getItem('shop_cart') || '[]');
        const count = cart.reduce((sum, item) => sum + item.quantity, 0);
        setCartCount(count);
      } catch (error) {
        setCartCount(0);
      }
    };

    // Initial load
    updateCartCount();

    // Listen for storage changes (cart updates)
    const interval = setInterval(updateCartCount, 500);
    window.addEventListener('storage', updateCartCount);

    return () => {
      clearInterval(interval);
      window.removeEventListener('storage', updateCartCount);
    };
  }, [isShopRoute, location.pathname]);

  // Close user menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setShowUserMenu(false);
      }
    };

    if (showUserMenu) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showUserMenu]);

  // Close category menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (categoryMenuRef.current && !categoryMenuRef.current.contains(event.target)) {
        setShowCategoryMenu(false);
      }
    };

    if (showCategoryMenu) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showCategoryMenu]);

  const handleLogout = () => {
    logout();
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/shop/search?q=${encodeURIComponent(searchQuery.trim())}`);
      setSearchQuery('');
    }
  };

  // Route optimizer breadcrumb state
  const [roBreadcrumb, setRoBreadcrumb] = useState('');

  useEffect(() => {
    if (activeApp !== 'routeoptimizer') return;
    const path = location.pathname;
    const weekMatch = path.match(/\/routeoptimizer\/week\/([^/]+)/);
    const branchMatch = path.match(/\/routeoptimizer\/branch\/([^/]+)/);

    if (weekMatch) {
      apiService.get(`/routeoptimizer/weeks/${weekMatch[1]}/details`)
        .then(d => {
          const parts = [d.company_name, d.branch_name, d.name].filter(Boolean);
          setRoBreadcrumb(parts.join(' / '));
        })
        .catch(() => setRoBreadcrumb(''));
    } else if (branchMatch) {
      // Try to resolve branch parent from companies
      setRoBreadcrumb('');
    } else {
      setRoBreadcrumb('');
    }
  }, [activeApp, location.pathname]);

  // Get domain name from window location (subdomain only)
  const domainName = window.location.hostname.split('.')[0];
  // Uppercase the domain name (e.g., "certus" -> "CERTUS")
  const displayName = domainName.toUpperCase();
  const isRouteOptimizer = activeApp === 'routeoptimizer';

  // Determine home path based on route
  const getHomePath = () => {
    if (isShopRoute) return '/shop';
    if (activeApp) return `/${activeApp}`;
    return '/';
  };

  return (
    <StyledHeader className={isRouteOptimizer ? 'ro-header-bar' : ''}>
      {isImpersonating && (
        <div style={{
          position: 'absolute', top: 0, left: 0, right: 0,
          background: '#ff9800', color: '#000', textAlign: 'center',
          padding: '2px 0', fontSize: '12px', fontWeight: 600, zIndex: 1001,
        }}>
          Viewing as {user?.name || 'another user'} (admin: {originalUserName || 'you'})
          <button onClick={handleExitImpersonation} style={{
            marginLeft: '12px', background: '#fff', border: 'none',
            borderRadius: '3px', padding: '1px 8px', cursor: 'pointer',
            fontSize: '11px', fontWeight: 600,
          }}>Exit</button>
        </div>
      )}
      <Flex align="center" gap="var(--space-3)">
        {isRouteOptimizer ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px', minWidth: 0 }}>
            <Link to="/routeoptimizer" style={{ textDecoration: 'none', whiteSpace: 'nowrap' }}>
              <span style={{ fontSize: '18px', fontWeight: '600', color: '#fff' }}>
                CERTUS Route Optimization
              </span>
              <span style={{ fontSize: '13px', color: 'rgba(255,255,255,0.7)', marginLeft: '8px' }}>
                Weekly Route Planner
              </span>
            </Link>
            {roBreadcrumb && (
              <span style={{ fontSize: '13px', color: 'rgba(255,255,255,0.85)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                <span style={{ margin: '0 8px', opacity: 0.5 }}>|</span>
                {roBreadcrumb}
              </span>
            )}
          </div>
        ) : (
          <HeaderTitle
            as={Link}
            to={getHomePath()}
            style={{
              cursor: 'pointer',
              textDecoration: 'none',
              color: 'var(--text-inverse)'
            }}
          >
            {displayName}
          </HeaderTitle>
        )}
        
        {/* Category menu for shop routes */}
        {isShopRoute && categories.length > 0 && (
          <div style={{ position: 'relative', marginLeft: 'var(--space-4)' }} ref={categoryMenuRef}>
            <button
              onClick={() => setShowCategoryMenu(!showCategoryMenu)}
              style={{
                padding: '8px 16px',
                borderRadius: '4px',
                border: '1px solid rgba(255,255,255,0.3)',
                backgroundColor: showCategoryMenu ? 'rgba(255,255,255,0.25)' : 'rgba(255,255,255,0.15)',
                color: 'white',
                fontSize: '14px',
                fontWeight: '500',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                transition: 'all 0.2s'
              }}
              onMouseEnter={(e) => {
                if (!showCategoryMenu) e.target.style.backgroundColor = 'rgba(255,255,255,0.2)';
              }}
              onMouseLeave={(e) => {
                if (!showCategoryMenu) e.target.style.backgroundColor = 'rgba(255,255,255,0.15)';
              }}
            >
              <span>📁</span>
              <span>Categories</span>
              <span style={{ fontSize: '10px' }}>{showCategoryMenu ? '▲' : '▼'}</span>
            </button>
            
            {showCategoryMenu && (
              <div style={{
                position: 'absolute',
                top: '100%',
                left: 0,
                marginTop: '8px',
                backgroundColor: 'white',
                border: '1px solid #e0e0e0',
                borderRadius: '4px',
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                minWidth: '220px',
                maxHeight: '400px',
                overflowY: 'auto',
                zIndex: 1000
              }}>
                <div 
                  style={{
                    padding: '12px 16px',
                    cursor: 'pointer',
                    color: '#333',
                    fontSize: '14px',
                    fontWeight: '600',
                    borderBottom: '1px solid #f0f0f0',
                    transition: 'background-color 0.2s'
                  }}
                  onClick={() => { 
                    navigate('/shop/catalog'); 
                    setShowCategoryMenu(false); 
                  }}
                  onMouseEnter={(e) => e.target.style.backgroundColor = '#f5f5f5'}
                  onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                >
                  📋 All Products
                </div>
                {categories.map(cat => (
                  <div 
                    key={cat.id}
                    style={{
                      padding: '12px 16px',
                      cursor: 'pointer',
                      color: '#333',
                      fontSize: '14px',
                      transition: 'background-color 0.2s'
                    }}
                    onClick={() => { 
                      navigate(`/shop/catalog?category=${cat.id}`); 
                      setShowCategoryMenu(false); 
                    }}
                    onMouseEnter={(e) => e.target.style.backgroundColor = '#f5f5f5'}
                    onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                  >
                    {cat.name}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
        
        {/* Search bar for shop routes */}
        {isShopRoute && (
          <form onSubmit={handleSearch} style={{ 
            marginLeft: 'var(--space-4)',
            flex: '0 1 400px',
            maxWidth: '400px'
          }}>
            <div style={{
              position: 'relative',
              width: '100%'
            }}>
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search products..."
                style={{
                  width: '100%',
                  padding: '8px 40px 8px 12px',
                  borderRadius: '4px',
                  border: '1px solid rgba(255,255,255,0.3)',
                  backgroundColor: 'rgba(255,255,255,0.15)',
                  color: 'white',
                  fontSize: '14px',
                  outline: 'none',
                  transition: 'all 0.2s'
                }}
                onFocus={(e) => {
                  e.target.style.backgroundColor = 'rgba(255,255,255,0.25)';
                  e.target.style.borderColor = 'rgba(255,255,255,0.5)';
                }}
                onBlur={(e) => {
                  e.target.style.backgroundColor = 'rgba(255,255,255,0.15)';
                  e.target.style.borderColor = 'rgba(255,255,255,0.3)';
                }}
              />
              <button
                type="submit"
                style={{
                  position: 'absolute',
                  right: '8px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'none',
                  border: 'none',
                  color: 'white',
                  cursor: 'pointer',
                  fontSize: '18px',
                  padding: '4px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                🔍
              </button>
            </div>
          </form>
        )}
      </Flex>
      
      <HeaderActions>
        {/* Cart link for shop routes */}
        {isShopRoute && (
          <Link to="/shop/cart" style={{ 
            color: 'white', 
            textDecoration: 'none', 
            fontSize: '24px',
            marginRight: '16px',
            position: 'relative',
            display: 'flex',
            alignItems: 'center'
          }}>
            <span>🛒</span>
            {cartCount > 0 && (
              <span style={{
                position: 'absolute',
                top: '-8px',
                right: '-8px',
                background: '#FFA500',
                color: 'white',
                borderRadius: '12px',
                padding: '2px 8px',
                fontSize: '12px',
                fontWeight: '600',
                minWidth: '20px',
                textAlign: 'center'
              }}>
                {cartCount}
              </span>
            )}
          </Link>
        )}
        
        {user ? (
          <div style={{ position: 'relative' }} ref={userMenuRef}>
            <Flex 
              align="center" 
              gap="var(--space-2)" 
              style={{ 
                cursor: 'pointer',
                padding: '8px 12px',
                borderRadius: '4px',
                transition: 'background-color 0.2s',
                backgroundColor: showUserMenu ? 'rgba(255,255,255,0.1)' : 'transparent'
              }}
              onClick={() => setShowUserMenu(!showUserMenu)}
            >
              <Flex align="center" gap="var(--space-1)" style={{ color: 'var(--text-inverse)' }}>
                <span style={{ fontSize: 'var(--font-size-body)', fontWeight: 'var(--font-weight-semibold)' }}>
                  {user.name}
                </span>
                <span style={{ fontSize: 'var(--font-size-label)', opacity: 0.8 }}>
                  ({user.role}{user.mode === 'read' ? ', read-only' : ''})
                </span>
              </Flex>
              <span style={{ color: 'white', fontSize: '12px' }}>
                {showUserMenu ? '▲' : '▼'}
              </span>
            </Flex>
            
            {showUserMenu && (
              <div style={{
                position: 'absolute',
                top: '100%',
                right: 0,
                marginTop: '8px',
                backgroundColor: 'white',
                border: '1px solid #e0e0e0',
                borderRadius: '4px',
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                minWidth: '200px',
                zIndex: 1000
              }}>
                {isShopRoute && (
                  <>
                    <div 
                      style={{
                        padding: '12px 16px',
                        cursor: 'pointer',
                        borderBottom: '1px solid #f0f0f0',
                        color: '#333',
                        fontSize: '14px',
                        transition: 'background-color 0.2s'
                      }}
                      onClick={() => { navigate('/shop/orders'); setShowUserMenu(false); }}
                      onMouseEnter={(e) => e.target.style.backgroundColor = '#f5f5f5'}
                      onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                    >
                      📦 My Orders
                    </div>
                    <div 
                      style={{
                        padding: '12px 16px',
                        cursor: 'pointer',
                        borderBottom: '1px solid #f0f0f0',
                        color: '#333',
                        fontSize: '14px',
                        transition: 'background-color 0.2s'
                      }}
                      onClick={() => { navigate('/shop/wishlist'); setShowUserMenu(false); }}
                      onMouseEnter={(e) => e.target.style.backgroundColor = '#f5f5f5'}
                      onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                    >
                      ❤️ Wishlist
                    </div>
                    <div 
                      style={{
                        padding: '12px 16px',
                        cursor: 'pointer',
                        borderBottom: '1px solid #f0f0f0',
                        color: '#333',
                        fontSize: '14px',
                        transition: 'background-color 0.2s'
                      }}
                      onClick={() => { navigate('/shop/account'); setShowUserMenu(false); }}
                      onMouseEnter={(e) => e.target.style.backgroundColor = '#f5f5f5'}
                      onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                    >
                      ⚙️ Account Settings
                    </div>
                    {user.role === 'admin' && (
                      <div 
                        style={{
                          padding: '12px 16px',
                          cursor: 'pointer',
                          borderBottom: '1px solid #f0f0f0',
                          color: '#333',
                          fontSize: '14px',
                          fontWeight: '600',
                          transition: 'background-color 0.2s'
                        }}
                        onClick={() => { navigate('/shop/admin'); setShowUserMenu(false); }}
                        onMouseEnter={(e) => e.target.style.backgroundColor = '#f5f5f5'}
                        onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                      >
                        🛠️ Shop Admin
                      </div>
                    )}
                  </>
                )}
                <div 
                  style={{
                    padding: '12px 16px',
                    cursor: 'pointer',
                    color: '#d32f2f',
                    fontSize: '14px',
                    fontWeight: '500',
                    transition: 'background-color 0.2s'
                  }}
                  onClick={() => { handleLogout(); setShowUserMenu(false); }}
                  onMouseEnter={(e) => e.target.style.backgroundColor = '#fff5f5'}
                  onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                >
                  🚪 Logout
                </div>
              </div>
            )}
          </div>
        ) : (
          <Flex align="center" gap="var(--space-2)">
            <PrimaryButton as={Link} to="/login">
              Login
            </PrimaryButton>
            <SecondaryButton as={Link} to="/register">
              Sign Up
            </SecondaryButton>
          </Flex>
        )}
      </HeaderActions>
    </StyledHeader>
  );
};

export default Header;
